import React, { useState, useEffect } from 'react';
import { Textarea } from './textarea';
import { SaveIcon, CheckCircle2 } from 'lucide-react';

interface PersistentTextareaProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    storageKey: string;
  }
  
  const PersistentTextarea: React.FC<PersistentTextareaProps> = ({
    value,
    onChange,
    placeholder = "Enter your custom prompt...",
    className = "",
    storageKey  // Add this to the destructured props
  }) => {
    const [isSaved, setIsSaved] = useState(true);
    const [saveMessage, setSaveMessage] = useState('');
    const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  
    // Debounce save indicator
    useEffect(() => {
      if (value) {
        setIsSaved(false);
        const timer = setTimeout(() => {
          setIsSaved(true);
          setLastSavedTime(new Date());
          localStorage.setItem(`textarea_${storageKey}`, value);  // Use storageKey here
        }, 1000);
  
        return () => clearTimeout(timer);
      }
    }, [value, storageKey]);  // Add storageKey to dependencies
  
    // Load saved content on mount
    useEffect(() => {
      const savedContent = localStorage.getItem(`textarea_${storageKey}`);  // Use storageKey here
      if (savedContent && !value) {
        onChange(savedContent);
      }
    }, [storageKey]); // Add storageKey to dependencies
  
    // Update save message
    useEffect(() => {
      if (lastSavedTime) {
        const updateSaveTime = () => {
          const now = new Date();
          const diff = now.getTime() - lastSavedTime.getTime();
          
          if (diff < 60000) { // less than a minute
            setSaveMessage('Saved');
          } else if (diff < 3600000) { // less than an hour
            const minutes = Math.floor(diff / 60000);
            setSaveMessage(`Saved ${minutes}m ago`);
          } else {
            const hours = Math.floor(diff / 3600000);
            setSaveMessage(`Saved ${hours}h ago`);
          }
        };
  
        updateSaveTime();
        const interval = setInterval(updateSaveTime, 60000); // Update every minute
        return () => clearInterval(interval);
      }
    }, [lastSavedTime]);
  
    return (
      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`min-h-[100px] resize-y pr-20 ${className}`}
        />
        <div className="absolute right-3 bottom-3 flex items-center gap-1.5 text-xs font-medium">
          {!isSaved ? (
            <div className="text-blue-600 flex items-center gap-1">
              <SaveIcon className="w-3.5 h-3.5 animate-pulse" />
              <span>Saving</span>
            </div>
          ) : (
            <div className="text-green-600 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{saveMessage}</span>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  export default PersistentTextarea;